import React, { useCallback, useMemo } from "react";
import {
  Box,
  Image,
  Text,
  Flex,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import Performerr from "../images/Menu/Performerr.png";
import Tsuzi from "../images/Assets/Tsuzi.jpg";
import { TEXTS } from "../constants/texts";
import { useInView } from "react-intersection-observer";

interface CommonImageProps {
  onContextMenu: (e: { preventDefault: () => void }) => void;
  draggable: boolean;
}

const preventDefault = (e: { preventDefault: () => void }): void => {
  e.preventDefault();
};

const commonImageProps: CommonImageProps = {
  onContextMenu: preventDefault,
  draggable: false,
};

interface PerformerSectionProps {
  scrollDirection: "up" | "down";
}

const PerformerSection: React.FC<PerformerSectionProps> = ({
  scrollDirection,
}) => {
  // Intersection Observer設定
  const commonInViewConfig = useMemo(
    () => ({
      threshold: 0.3,
      triggerOnce: false,
    }),
    []
  );

  const { ref: performerRef, inView: performerInView } =
    useInView(commonInViewConfig);
  const { ref: performerTitleRef, inView: performerTitleInView } =
    useInView(commonInViewConfig);
  const { ref: performerImageRef, inView: performerImageInView } = useInView({
    ...commonInViewConfig,
    threshold: 0.5,
  });
  const { ref: performerTextRef, inView: performerTextInView } =
    useInView(commonInViewConfig);
  const { ref: performerOrchestraRef, inView: performerOrchestraInView } =
    useInView(commonInViewConfig);

  // バリアント定義
  const slideInVariants = useMemo(
    () => ({
      hidden: { opacity: 0, y: 20 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" },
      },
      instant: { opacity: 1, y: 0, transition: { duration: 0 } },
    }),
    []
  );

  const slideInFromLeftVariants = useMemo(
    () => ({
      hidden: { opacity: 0, x: -50 },
      visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 1.2, ease: "easeInOut" },
      },
      instant: { opacity: 1, x: 0, transition: { duration: 0 } },
    }),
    []
  );

  // 画面サイズに応じてアニメーションバリアントを切り替え
  const titleVariants =
    useBreakpointValue({
      base: slideInVariants,
      sm: slideInVariants,
      md: slideInFromLeftVariants,
    }) || slideInFromLeftVariants;

  // アニメーション状態決定のためのヘルパー関数
  const getAnimationState = useCallback(
    (isInView: boolean) => {
      if (!isInView) return "hidden";
      return scrollDirection === "up" ? "instant" : "visible";
    },
    [scrollDirection]
  );

  const commonImageSizes = {
    width: {
      base: "270px",
      sm: "320px",
      md: "270px",
      lg: "350px",
      xl: "400px",
    },
    maxWidth: {
      base: "100%",
      md: "900px",
      lg: "1100px",
      xl: "1200px",
    },
  };

  return (
    <Box id="performer-section" pos="relative" w="100%">
      <Box
        ref={performerRef}
        width="100%"
        textAlign="center"
        mb={{
          base: "40px",
          sm: "50px",
          md: "60px",
          lg: "80px",
        }}
      >
        <motion.div
          initial="hidden"
          animate={getAnimationState(performerInView)}
          variants={slideInVariants}
          style={{ width: "100%" }}
        >
          <Image
            src={Performerr}
            alt="performer"
            w={{
              base: "90%",
              sm: "80%",
              md: "80%",
              lg: "80%",
              xl: "70%",
              "2xl": "65%",
            }}
            h="auto"
            maxW={{
              base: "360px",
              sm: "450px",
              md: "700px",
              lg: "900px",
              xl: "1200px",
              "2xl": "1200px",
            }}
            mx="auto"
            display="block"
            {...commonImageProps}
          />
        </motion.div>
      </Box>

      <Box
        width="100%"
        maxW={{
          base: "95%",
          sm: "90%",
          md: "80%",
          lg: "70%",
          xl: "70%",
          "2xl": "70%",
        }}
        mx="auto"
      >
        <VStack align="center" spacing={4} w="100%">
          <Box
            ref={performerTitleRef}
            width="100%"
            maxW={commonImageSizes.maxWidth}
            mx="auto"
          >
            <motion.div
              initial="hidden"
              animate={getAnimationState(performerTitleInView)}
              variants={titleVariants}
              style={{ width: "100%" }}
            >
              <Flex
                direction="column"
                align={{
                  base: "center",
                  md: "flex-start",
                }}
                maxW={{
                  base: "270px",
                  sm: "320px",
                  md: "400px",
                  lg: "400px",
                }}
                mx={{
                  base: "auto",
                  sm: "auto",
                  md: "0",
                }}
                width="100%"
              >
                <Text
                  color="white"
                  fontSize={{
                    base: "12px",
                    sm: "14px",
                    md: "18px",
                    lg: "20px",
                    xl: "25px",
                    "2xl": "25px",
                  }}
                  fontWeight="bold"
                  px={7}
                  py={1}
                  borderRadius="3xl"
                  w="fit-content"
                  bg="rgb(60, 17, 0)"
                  mb={{
                    base: 2,
                    sm: 2,
                    md: 4,
                    lg: 4,
                    xl: 4,
                    "2xl": 4,
                  }}
                  textAlign={{
                    base: "center",
                    sm: "center",
                    md: "left",
                  }}
                >
                  {TEXTS.Performer.content[0]}
                </Text>

                <Flex
                  align="center"
                  gap={4}
                  justify={{
                    base: "center",
                    sm: "center",
                    md: "flex-start",
                  }}
                  width="100%"
                  flexWrap="wrap"
                >
                  <Text
                    color="white"
                    fontSize={{
                      base: "18px",
                      sm: "21px",
                      md: "28px",
                      lg: "30px",
                      xl: "40px",
                      "2xl": "40px",
                    }}
                    fontWeight="bold"
                  >
                    {TEXTS.Performer.content[1]}
                  </Text>
                  <Text
                    color="white"
                    fontSize={{
                      base: "12px",
                      sm: "14px",
                      md: "18px",
                      lg: "20px",
                      xl: "25px",
                      "2xl": "25px",
                    }}
                  >
                    {TEXTS.Performer.content[2]}
                  </Text>
                </Flex>
              </Flex>
            </motion.div>
          </Box>

          <Flex
            gap={{
              base: 8,
              sm: 8,
              md: 8,
              lg: 10,
              xl: 10,
              "2xl": 0,
            }}
            align="flex-start"
            direction={{
              base: "column",
              sm: "column",
              md: "row",
            }}
            alignItems={{
              base: "center",
              sm: "center",
              md: "flex-start",
            }}
            justifyContent="center"
            mt={{
              base: 4,
              sm: 6,
              md: 8,
              lg: 10,
            }}
            width="100%"
            maxW={commonImageSizes.maxWidth}
            mx="auto"
            px={{
              base: 0,
              sm: 0,
              md: 0,
            }}
          >
            <Box
              ref={performerImageRef}
              width={{
                base: "100%",
                md: "45%",
                lg: "40%",
              }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={
                  performerImageInView
                    ? scrollDirection === "up"
                      ? { opacity: 1, transition: { duration: 0 } }
                      : {
                          opacity: 1,
                          transition: { duration: 1, ease: "easeOut" },
                        }
                    : { opacity: 0, transition: { duration: 0 } }
                }
                style={{ width: "100%" }}
              >
                <Box
                  maxW={commonImageSizes.width}
                  mx={{
                    base: "auto",
                    sm: "auto",
                    md: "0",
                  }}
                  position="relative"
                >
                  <Box
                    width={commonImageSizes.width}
                    height="auto"
                    overflow="hidden"
                    position="relative"
                  >
                    <motion.div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        height: "100%",
                        overflow: "hidden",
                      }}
                      initial={{ opacity: 0 }}
                      animate={
                        performerImageInView
                          ? scrollDirection === "up"
                            ? { opacity: 1, transition: { duration: 0 } }
                            : {
                                opacity: 1,
                                transition: { duration: 1, ease: "easeOut" },
                              }
                          : { opacity: 0, transition: { duration: 0 } }
                      }
                    >
                      <motion.div
                        initial={{ scale: 1.2 }}
                        animate={
                          performerImageInView
                            ? scrollDirection === "up"
                              ? { scale: 1, transition: { duration: 0 } }
                              : {
                                  scale: 1,
                                  transition: {
                                    duration: 1,
                                    ease: "easeInOut",
                                  },
                                }
                            : { scale: 1.2, transition: { duration: 0 } }
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          transformOrigin: "center",
                        }}
                      >
                        <Image
                          src={Tsuzi}
                          alt="Tsuzi"
                          width="100%"
                          height="auto"
                          {...commonImageProps}
                        />
                      </motion.div>
                    </motion.div>
                  </Box>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={
                      performerImageInView
                        ? scrollDirection === "up"
                          ? { opacity: 1, y: 0, transition: { duration: 0 } }
                          : {
                              opacity: 1,
                              y: 0,
                              transition: {
                                duration: 0.5,
                                ease: "easeOut",
                                delay: 0.3,
                              },
                            }
                        : { opacity: 0, y: 20, transition: { duration: 0 } }
                    }
                  >
                    <Flex
                      justify="flex-end"
                      align="center"
                      gap={1}
                      mt={{
                        base: 1,
                        sm: 1,
                        md: 1,
                      }}
                      color="black"
                      fontSize={{
                        base: "10px",
                        sm: "10px",
                        md: "10px",
                        lg: "14px",
                        xl: "14px",
                      }}
                      width="100%"
                      position="relative"
                      right={0}
                      textAlign="right"
                      maxW={commonImageSizes.width}
                      mb={{
                        base: 4,
                        sm: 4,
                        md: 0,
                      }}
                    >
                      <Text>{TEXTS.Performer.content[8]}</Text>
                      <Text>{TEXTS.Performer.content[9]}</Text>
                    </Flex>
                  </motion.div>
                </Box>
              </motion.div>
            </Box>

            <Box
              ref={performerTextRef}
              width={{
                base: "100%",
                md: "55%",
                lg: "60%",
                xl: "60%",
                "2xl": "60%",
              }}
            >
              <motion.div
                initial="hidden"
                animate={getAnimationState(performerTextInView)}
                variants={slideInVariants}
                style={{ width: "100%" }}
              >
                <VStack
                  spacing={0}
                  align={{
                    base: "center",
                    md: "flex-start",
                  }}
                  width="100%"
                >
                  {TEXTS.Performer.content.slice(3, 6).map((text, index) => (
                    <Text
                      key={`performer-description-${index}`}
                      color="white"
                      fontSize={{
                        base: "11px",
                        sm: "11px",
                        md: "13px",
                        lg: "13px",
                        xl: "18px",
                        "2xl": "20px",
                      }}
                      lineHeight="1.5"
                      textAlign="left"
                      whiteSpace="pre-line"
                      width="100%"
                      px={{
                        base: "4",
                        sm: "4",
                        md: "0",
                      }}
                    >
                      {text}
                    </Text>
                  ))}
                </VStack>
              </motion.div>
            </Box>
          </Flex>

          <Box
            ref={performerOrchestraRef}
            width="100%"
            maxW={commonImageSizes.maxWidth}
            mx="auto"
            mt={{
              base: 8,
              sm: 10,
              md: 12,
              lg: 16,
            }}
            mb={{
              base: 10,
              sm: 12,
              md: 16,
              lg: 20,
            }}
          >
            <motion.div
              initial="hidden"
              animate={getAnimationState(performerOrchestraInView)}
              variants={{
                hidden: {
                  opacity: 0,
                  x: useBreakpointValue({ base: 0, md: -50 }),
                  y: useBreakpointValue({ base: 20, md: 0 }),
                  transition: { duration: 0 },
                },
                visible: {
                  opacity: 1,
                  x: 0,
                  y: 0,
                  transition: { duration: 1.2, ease: "easeInOut" },
                },
                instant: {
                  opacity: 1,
                  x: 0,
                  y: 0,
                  transition: { duration: 0 },
                },
              }}
              style={{ width: "100%" }}
            >
              <Flex
                direction="column"
                align={{
                  base: "center",
                  md: "flex-start",
                }}
                maxW={{
                  base: "270px",
                  sm: "320px",
                  md: "400px",
                  lg: "400px",
                }}
                mx={{
                  base: "auto",
                  sm: "auto",
                  md: "0",
                }}
              >
                <Text
                  color="white"
                  fontSize={{
                    base: "12px",
                    sm: "14px",
                    md: "18px",
                    lg: "20px",
                    xl: "25px",
                    "2xl": "25px",
                  }}
                  fontWeight="bold"
                  px={7}
                  py={1}
                  borderRadius="3xl"
                  w="fit-content"
                  bg="rgb(60, 17, 0)"
                  textAlign={{
                    base: "center",
                    md: "left",
                  }}
                >
                  {TEXTS.Performer.content[6]}
                </Text>
                <Text
                  color="white"
                  fontWeight="bold"
                  fontSize={{
                    base: "18px",
                    sm: "21px",
                    md: "28px",
                    lg: "30px",
                    xl: "40px",
                    "2xl": "40px",
                  }}
                  textAlign={{
                    base: "center",
                    md: "left",
                  }}
                  whiteSpace={{
                    base: "normal",
                    md: "nowrap",
                  }}
                  mt={{
                    base: 2,
                    sm: 3,
                    md: 4,
                    lg: 5,
                  }}
                >
                  {TEXTS.Performer.content[7]}
                </Text>
              </Flex>
            </motion.div>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default PerformerSection;
